import { Component, Input, Output, EventEmitter, ViewEncapsulation, inject, computed, signal } from '@angular/core';

import { Router, NavigationEnd } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatIconModule } from '@angular/material/icon';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';
import { UserComponent } from '../user/user.component';
import { TeamContextService } from '../../../core/services/team-context.service';
import { LeagueContextService } from '../../../core/services/league-context.service';
import { TeamPickerSheetComponent } from '../team-picker-sheet/team-picker-sheet.component';
import { TEAM_COLOR_CLASS } from '../../../core/services/team-context.utils';
import { Team } from '../../../interfaces/database/team.interface';
import { SeasonStatus } from '../../../enums/convocatoria.enum';

@Component({
    selector: 'ph-header',
    standalone: true,
    imports: [
    MatButtonModule,
    MatIconModule,
    UserComponent
],
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent {
    @Input() showMenuToggle = false;
    @Output() menuToggle = new EventEmitter<void>();

    private _router = inject(Router);
    private _bottomSheet = inject(MatBottomSheet);
    readonly teamContext = inject(TeamContextService);
    private readonly _leagueContext = inject(LeagueContextService);

    private readonly _currentUrl = toSignal(
        this._router.events.pipe(
            filter(e => e instanceof NavigationEnd),
            map((e: NavigationEnd) => e.url),
            startWith(this._router.url),
        ),
        { initialValue: this._router.url },
    );

    readonly showTeamSwitcher = computed(() =>
        !this._currentUrl().startsWith('/torneos')
    );

    readonly isPastSeason = computed(() =>
        this._leagueContext.selectedSeason()?.status === SeasonStatus.SEASON_ENDED
    );

    readonly pastSeasonYear = computed(() =>
        this._leagueContext.selectedSeason()?.year ?? null
    );

    readonly isPickerOpen = signal(false);

    teamColorClass(team: Team): string {
        return TEAM_COLOR_CLASS[team.colorKey] ?? TEAM_COLOR_CLASS['blue'];
    }

    onMenuToggle(): void {
        this.menuToggle.emit();
    }

    openTeamPicker(): void {
        this.isPickerOpen.set(true);
        const ref = this._bottomSheet.open(TeamPickerSheetComponent, {
            data: {
                teams: this.teamContext.availableTeams(),
                selectedId: this.teamContext.selectedTeamId(),
            },
        });
        ref.afterDismissed().subscribe((id: string | undefined) => {
            this.isPickerOpen.set(false);
            if (id) this.teamContext.selectTeam(id);
        });
    }
}
